import { TCompressPdfResDto } from "@/api/services/global/file/dtos/compress-pdf.dto";
import { TGetFileInfoResDto } from "@/api/services/global/file/dtos/get-file-info.dto";
import { TGetSingedUrlResDto } from "@/api/services/global/file/dtos/get-signed-url";
import { TUploadFileResDto } from "@/api/services/global/file/dtos/upload-file.dto";
import { SIGNED_URL_STALE_TIME, TFileUploadFor } from "@/constants/file";
import { QUERY_KEYS } from "@/constants/queryKeys";
import { globalApi } from "@/utils/api";
import { createUrlWithParams } from "@/utils/url";
import { QueryClient, useQuery } from "@tanstack/react-query";
import { AxiosProgressEvent } from "axios";

export const uploadTalentExternalFileApi = async (payload: {
  uploadFor: TFileUploadFor;
  file: File;
  token?: string;
  talentId?: string;
}): Promise<TUploadFileResDto> => {
  const formData = new FormData();
  formData.append("file", payload.file);
  const url = createUrlWithParams("/files/upload-file", { uploadFor: payload.uploadFor, token: payload.token, talentId: payload.talentId });
  const rs = await globalApi.post(url, formData);
  return rs.data;
};

export const uploadFileApi = async (
  payload: { uploadFor: TFileUploadFor; file: File; token?: string },
  onUploadProgress?: (event: AxiosProgressEvent) => void,
): Promise<TUploadFileResDto> => {
  const formData = new FormData();
  formData.append("file", payload.file);
  const url = createUrlWithParams("/files/upload-file", { uploadFor: payload.uploadFor, token: payload.token });
  const rs = await globalApi.post(url, formData, {
    onUploadProgress,
  });
  return rs.data;
};

export const fetchFileInfoApi = async (filePath: string): Promise<TGetFileInfoResDto> => {
  const url = createUrlWithParams("/files", { filePath });
  const rs = await globalApi.get(url);
  return rs.data;
};

export const useFileInfo = (filePath?: string) => {
  return useQuery({
    queryKey: [QUERY_KEYS.FILE.FETCH_INFO, filePath],
    queryFn: () => (filePath ? fetchFileInfoApi(filePath) : undefined),
    enabled: Boolean(filePath),
  });
};

export const fetchSignedUrlApi = async (filePath: string, size?: string): Promise<TGetSingedUrlResDto> => {
  const url = createUrlWithParams("/files/signed-url", { filePath, size });
  const rs = await globalApi.get(url);
  return rs.data;
};

type TUseFileSignedUrlOptions = {
  size?: string;
  staleTime?: number;
};

export const useFileSignedUrl = (filePath?: string | null, options?: TUseFileSignedUrlOptions) => {
  const { size, staleTime = 0 } = options ?? {};
  return useQuery({
    queryKey: [QUERY_KEYS.FILE.FETCH_SIGNED_URL, filePath, size],
    queryFn: () => (filePath ? fetchSignedUrlApi(filePath, size) : undefined),
    enabled: Boolean(filePath),
    staleTime,
  });
};

/**
 * Seed the `useFileSignedUrl` cache with URLs already signed by BE (e.g. vocabulary list `imageUrl`),
 * so list items render images without one GET /files/signed-url per item (N+1).
 * A still-fresh entry is kept as-is: presigned URLs change on every signing, and a stable `src`
 * lets the browser reuse the downloaded image across list refetches.
 */
export const primeFileSignedUrlCache = (queryClient: QueryClient, items: { filePath: string | null; signedUrl: string | null }[]) => {
  const now = Date.now();
  items.forEach(({ filePath, signedUrl }) => {
    if (!filePath || !signedUrl) return;
    // Same key shape as useFileSignedUrl without `size`
    const queryKey = [QUERY_KEYS.FILE.FETCH_SIGNED_URL, filePath, undefined];
    const dataUpdatedAt = queryClient.getQueryState(queryKey)?.dataUpdatedAt ?? 0;
    if (now - dataUpdatedAt < SIGNED_URL_STALE_TIME) return;
    queryClient.setQueryData<TGetSingedUrlResDto>(queryKey, { data: { signedUrl } });
  });
};

export const compressPdfApi = async (
  payload: { targetSizeMb: number; file: File },
  onUploadProgress?: (event: AxiosProgressEvent) => void,
): Promise<TCompressPdfResDto> => {
  const formData = new FormData();
  formData.append("file", payload.file);
  const url = createUrlWithParams("/files/compress-pdf", { targetSizeMb: payload.targetSizeMb });
  const rs = await globalApi.post(url, formData, {
    onUploadProgress,
  });
  return rs.data;
};

export const fetchImageAsBase64 = async (url: string): Promise<string | null> => {
  try {
    const response = await fetch(url);
    if (!response.ok) return null;

    const blob = await response.blob();
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64 = reader.result as string;
        const base64Data = base64.split(",")[1];
        resolve(base64Data);
      };
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  } catch (error) {
    console.error("Error fetching image:", error);
    return null;
  }
};

export const getImageExtension = (url: string): string => {
  const extension = url.split(".").pop()?.toLowerCase() || "";
  if (["jpg", "jpeg"].includes(extension)) return "jpeg";
  if (extension === "png") return "png";
  return "jpeg";
};
